import { z } from 'zod';
/**
 * What a scanner is permitted to reach.
 *
 * These rules are **necessary but not sufficient**. A schema cannot stop DNS rebinding, a
 * hostname that resolves to a private address, or a redirect off-scope mid-scan. Those need
 * egress control at the network layer (§1: "authorised by construction… API, tools, network").
 * This is the first of those three gates, not the whole of it.
 */
/** Literal IP addresses, in either family. Used to reject them, not to accept them. */
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const LOOKS_LIKE_IPV6 = /^[0-9a-f]*:[0-9a-f:.]*$/i;
const LABEL = '[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?';
const HOSTNAME = new RegExp(`^${LABEL}(\\.${LABEL})+$`);
/**
 * A hostname a scan may target.
 *
 * IP literals are rejected outright rather than filtered. Allowing them would put the burden
 * on a denylist that has to be exhaustive — link-local (`169.254.169.254`, the cloud metadata
 * endpoint and a direct route to the scanner's own credentials), loopback, every private
 * range, and their IPv6 equivalents and encodings. Requiring a resolvable public hostname is
 * one rule instead of a list, and no legitimate engagement has needed an IP literal yet.
 *
 * Wildcards are rejected for the same reason: `*.example.com` silently widens scope every time
 * someone adds a subdomain, so each host is named explicitly and appears in the approved diff.
 */
export const TargetHostSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(4)
    .max(253)
    .refine((host) => !IPV4.test(host) && !LOOKS_LIKE_IPV6.test(host), {
    message: 'Name the host, not an IP address — IP literals are never in scope',
})
    .refine((host) => host !== 'localhost' && !host.endsWith('.localhost') && !host.endsWith('.local'), {
    message: 'Loopback and link-local names are never in scope',
})
    .refine((host) => !host.includes('*'), {
    message: 'Wildcards are not allowed — name each host so scope changes stay reviewable',
})
    .refine((host) => HOSTNAME.test(host), { message: 'Not a valid hostname' });
/**
 * Splits an http(s) URL into scheme and host without the `URL` global, which is not in this
 * package's lib — `packages/shared` is consumed by both the browser and Node, so it stays
 * free of environment-specific types.
 *
 * Returns null for anything it cannot read confidently, and the caller treats that as invalid.
 */
function splitUrl(raw) {
    const match = /^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)/i.exec(raw);
    if (!match)
        return null;
    const authority = match[2] ?? '';
    /*
     * Userinfo is rejected rather than parsed. `https://trusted.example.com@evil.test/` reads as
     * the trusted host to a human skimming an approval and resolves to the attacker's host —
     * the one ambiguity most likely to get a bad scope signed off. Scan credentials belong in
     * Secrets Manager, so nothing legitimate needs it.
     */
    if (authority.includes('@'))
        return null;
    // Strip the port; an IPv6 literal in brackets is rejected by TargetHostSchema anyway.
    const host = authority.replace(/:\d*$/, '').toLowerCase();
    if (host.length === 0)
        return null;
    return { scheme: (match[1] ?? '').toLowerCase(), host };
}
/**
 * A URL the spider may start from.
 *
 * Only http and https: a scheme like `file:` or `javascript:` is not a scan target, and
 * leaving the set open would make the scanner's URL handling the security boundary.
 */
export const TargetUrlSchema = z
    .url()
    .max(2048)
    .refine((raw) => {
    const parts = splitUrl(raw);
    return parts !== null && (parts.scheme === 'https' || parts.scheme === 'http');
}, { message: 'Only http and https URLs, with no embedded credentials, can be scanned' })
    .refine((raw) => {
    const parts = splitUrl(raw);
    return parts !== null && TargetHostSchema.safeParse(parts.host).success;
}, { message: 'The URL host is not a valid scan target' });
/** A path prefix, always absolute so it cannot be read as relative to an unexpected base. */
export const PathPrefixSchema = z
    .string()
    .trim()
    .min(1)
    .max(512)
    .refine((path) => path.startsWith('/'), { message: 'Paths must start with /' });
/** The host of a URL that has already passed `TargetUrlSchema`; '' if it cannot be read. */
export function hostOf(url) {
    return splitUrl(url)?.host ?? '';
}
//# sourceMappingURL=target.js.map